import moment from 'moment';
import { oneOf } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { isEmptyOrBlankString, isEmptyOrEmptyString } from '@philips-internal/oswald-ui-utils';
import { createMask, DATE_FORMATS, getDateFormat, getPattern as getPatternUtil, isMomentValid, isValid, toDate } from './date-utils.js';
const DATE = 'date';
export function format(value, options = {}) {
    if (isEmptyOrBlankString(value)) {
        return '';
    }
    const { type = DATE } = options;
    const { format, parseFormat } = getDateFormat(type);
    // accepts a moment instance
    if (moment.isMoment(value) && isMomentValid(value)) {
        return value.format(format);
    }
    // accepts an already formatted string
    if (isValid(value, format)) {
        /**
         * This handler is necessary because MomentJS accepts 4 different meridiem formats: AM/PM | am/pm | A/P |a/p.
         * Then to fix the informed meridiem case on the fly, we need to convert it to the formatter's type format
         */
        const hasMeridiem = String(value).match(/(am|pm)/i);
        if (hasMeridiem && hasMeridiem.length > 0) {
            return moment(value, format).format(format);
        }
        return value;
    }
    // accepts a string in the right parseformat
    if (isValid(value, parseFormat)) {
        return moment(toDate(value, parseFormat)).format(format);
    }
    // accepts unmasked and utc strings
    const mask = createMask(options);
    mask.value = String(value);
    return mask.value ?? value;
}
format.optionTypes = {
    type: oneOf(['', ...Object.keys(DATE_FORMATS)]),
};
export function parse(value, options = {}) {
    if (isEmptyOrBlankString(value)) {
        return '';
    }
    const { type = DATE } = options;
    const { parseFormat } = getDateFormat(type);
    // accepts a utc string
    if (isValid(value, parseFormat)) {
        return value;
    }
    // accepts unmasked and already formatted strings
    const masked = createMask(options);
    masked.value = String(value);
    if (masked.isComplete) {
        let complete = moment(masked.typedValue);
        if (type !== DATE) {
            complete = complete.utc();
        }
        if (typeof parseFormat === 'string') {
            return complete.format(parseFormat);
        }
        return complete.format(moment.defaultFormatUtc);
    }
    return value ?? '';
}
export function validate(value, options = {}) {
    const message = Array.isArray(value)
        ? 'tws.validator.invalid-date-interval'
        : 'tws.validator.invalid-date';
    if (isEmptyOrEmptyString(value)) {
        return [{ message, name: DATE, valid: true }];
    }
    const { type = DATE } = options;
    const { parseFormat } = getDateFormat(type);
    // accepts an utc string or an array of utc strings only, as this should
    // be the only values sent to or received from other services
    const valid = Array.isArray(value)
        ? value.every(utcString => isValid(utcString, parseFormat))
        : isValid(value, parseFormat);
    return [{
            message,
            name: DATE,
            valid,
        }];
}
export function getPattern(options = {}) {
    const { type = DATE } = options;
    const { format } = getDateFormat(type);
    return getPatternUtil(format);
}
const DateFormatter = {
    format,
    getPattern,
    parse,
    validate,
};
export { DATE, DateFormatter, };
export default DateFormatter;
//# sourceMappingURL=date.js.map